import { useCallback, useRef } from 'react';
import { useGetApiData } from '@grc/ui-utility';
import { BusinessEntityType } from '@grc/ui-shared/lib/components/models/enums/enums';
import { fetchBusinessEntities } from '../../service/api/fetch-business-entities';
import { IQuote } from '../../models/quotes/quote.interface';
import { IQuoteGridRow } from '../../models/quote-management/quote-grid.interface';
import { ILocationEntity } from '../../common/location-popover/location-popover.interface';

// Resolves the Origin/Destination GUIDs of one grid page into location
// records (same fetchBusinessEntities/BusinessEntityType.Location lookup as
// useShipmentEntities.ts). One lookup per page for the ids not seen yet;
// found locations are cached by id so paging back does not refetch them.
export function useQuoteEntities() {
  const { fetchData } = useGetApiData();
  const locationsById = useRef(new Map<string, ILocationEntity>());

  const enrichPage = useCallback(
    async (quotes: IQuote[]): Promise<IQuoteGridRow[]> => {
      const cache = locationsById.current;
      const missingIds = Array.from(
        new Set(
          quotes
            .flatMap(quote => [quote.originId, quote.destinationId])
            .filter((id): id is string => !!id && !cache.has(id))
        )
      );

      if (missingIds.length) {
        // fetchBusinessEntities toasts its own failures; unresolved ids show a dash
        const result = await fetchBusinessEntities({
          fetchData,
          entityIds: missingIds,
          entityType: BusinessEntityType.Location
        });
        Object.entries(
          (result?.map ?? {}) as Record<string, ILocationEntity>
        ).forEach(
          ([id, entity]) => cache.set(id, entity)
        );
      }

      return quotes.map(quote => ({
        ...quote,
        originLocation: cache.get(quote.originId) ?? null,
        destinationLocation: cache.get(quote.destinationId) ?? null
      }));
    },
    [fetchData]
  );

  return { enrichPage };
}

export default useQuoteEntities;
